import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Crown, CheckCircle, Upload, XCircle } from 'lucide-react';
import axios from 'axios';
import { formatPhoneNumber, isValidPhoneNumber, isValidEmail } from '../../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HostSignup = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Step 1: Space Information
    coworking_space_name: '',
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    phone_number: '',

    // Step 2: Contact Information
    contact_person: '',
    title: '',
    website_url: '',

    // Step 3: Promotional Information
    marketing_channels: [],
    cobranding_opportunities: '',
    mission_vision: '',
    community_values: '',
    testimonials: '',
    previous_events_info: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMultiSelect = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [name]: newValues };
    });
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.coworking_space_name) newErrors.coworking_space_name = 'Coworking space name is required';
      if (!formData.full_name) newErrors.full_name = 'Full name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zip_code) newErrors.zip_code = 'ZIP code is required';
      if (!formData.country) newErrors.country = 'Country is required';
    }

    if (step === 2) {
      if (!formData.contact_person) newErrors.contact_person = 'Contact person is required';
      if (!formData.title) newErrors.title = 'Title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      console.log('🔍 Host signup attempt:', {
        apiUrl: `${API}/auth/signup/host`,
        backendUrl: BACKEND_URL,
        email: formData.email
      });

      const signupData = {
        step1: {
          coworking_space_name: formData.coworking_space_name,
          full_name: formData.full_name,
          email: formData.email,
          password: formData.password,
          role: 'host',
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          country: formData.country,
          phone_number: formData.phone_number,
          // photos: formData.photos // Removed photo upload to prevent form issues
        },
        step2: {
          contact_person: formData.contact_person,
          title: formData.title,
          website_url: formData.website_url
        },
        step3: {
          marketing_channels: formData.marketing_channels,
          cobranding_opportunities: formData.cobranding_opportunities,
          mission_vision: formData.mission_vision,
          community_values: formData.community_values,
          testimonials: formData.testimonials,
          previous_events_info: formData.previous_events_info
        }
      };

      const response = await axios.post(`${API}/auth/signup/host`, signupData, {
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Host signup successful:', response.status);

      // Store user info for reference
      localStorage.setItem('signup_email', formData.email);
      localStorage.setItem('signup_role', 'host');

      // navigate(`/verify-email`, {
      //   state: {
      //     requiresPayment: response.data.requires_payment,
      //     message: response.data.message,
      //   }
      // });

      // Show success screen instead of alert
      setCurrentStep(4); // New success step

    } catch (error) {
      console.error('Host signup error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method
      });

      // Check for network/connection errors first
      if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || !error.response) {
        console.error('Network connection issue detected:', error.message);
        setErrors({
          general: `Network connection failed. Please check your internet connection and try again. Error: ${error.message || 'Unknown network error'}`
        });
        return;
      }

      // Handle HTTP response errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // Handle specific error cases
        if (detail === "Email already registered") {
          setErrors({
            general: "This email address is already registered. Please use a different email address or try logging in instead."
          });
          setCurrentStep(1); // Go back to first step to change email
        }
        // If it's a string, show it directly
        else if (typeof detail === 'string') {
          setErrors({ general: detail });
        }
        // If it's an array of validation errors
        else if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              return err.msg;
            }
            return String(err);
          }).join('. ');
          setErrors({ general: errorMessages });
        }
        // If it's an object with errors property
        else if (detail.errors && typeof detail.errors === 'object') {
          setErrors(detail.errors);
          setCurrentStep(1); // Go back to first step if validation errors
        }
        // Fallback for any other object structure
        else {
          setErrors({ general: 'Please check your information and try again.' });
        }
      } else {
        // Generic server error
        const statusCode = error.response?.status || 'Unknown';
        setErrors({
          general: `Server error (${statusCode}). Please try again. If the problem persists, contact support.`
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia',
    'Japan', 'South Korea', 'Singapore', 'India', 'Brazil', 'Mexico', 'Other'
  ];

  const marketingChannelOptions = [
    'Social Media', 'Email Marketing', 'Content Marketing', 'SEO', 'PPC Advertising',
    'Influencer Marketing', 'Community Events', 'Partnerships', 'Direct Sales'
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-4">Coworking Space Information</h3>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Name of Coworking Space *</label>
              <Input
                name="coworking_space_name"
                value={formData.coworking_space_name}
                onChange={handleInputChange}
                placeholder="Enter your coworking space name"
                className="bg-gray-800 border-gray-700 text-white"
              />
              {errors.coworking_space_name && <p className="text-red-500 text-sm mt-1">{errors.coworking_space_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Your Full Name *</label>
              <Input
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className="bg-gray-800 border-gray-700 text-white"
              />
              {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Email *</label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className="bg-gray-800 border-gray-700 text-white"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Password *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create password"
                    className="bg-gray-800 border-gray-700 text-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Confirm Password *</label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    placeholder="Confirm password"
                    className="bg-gray-800 border-gray-700 text-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirm_password && <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Address *</label>
              <Input
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter complete address"
                className="bg-gray-800 border-gray-700 text-white"
              />
              {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">City *</label>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  className="bg-gray-800 border-gray-700 text-white"
                />
                {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">State *</label>
                <Input
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  className="bg-gray-800 border-gray-700 text-white"
                />
                {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">ZIP *</label>
                <Input
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  placeholder="ZIP code"
                  className="bg-gray-800 border-gray-700 text-white"
                />
                {errors.zip_code && <p className="text-red-500 text-sm mt-1">{errors.zip_code}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Country *</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full h-10 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                >
                  <option value="">Select country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Phone Number</label>
              <Input
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Contact Person *</label>
              <Input
                name="contact_person"
                value={formData.contact_person}
                onChange={handleInputChange}
                placeholder="Enter contact person name"
                className="bg-gray-800 border-gray-700 text-white"
              />
              {errors.contact_person && <p className="text-red-500 text-sm mt-1">{errors.contact_person}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Title *</label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter title/position"
                className="bg-gray-800 border-gray-700 text-white"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Website URL</label>
              <Input
                name="website_url"
                value={formData.website_url}
                onChange={handleInputChange}
                placeholder="https://example.com"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-4">Promotional Information</h3>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Marketing Channels Available</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {marketingChannelOptions.map(channel => (
                  <div key={channel} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.marketing_channels.includes(channel)}
                      onChange={() => handleMultiSelect('marketing_channels', channel)}
                      className="rounded border-gray-600 text-amber-600 focus:ring-amber-600"
                    />
                    <label className="text-sm text-gray-300">{channel}</label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Co-Branding Opportunities</label>
              <Textarea
                name="cobranding_opportunities"
                value={formData.cobranding_opportunities}
                onChange={handleInputChange}
                placeholder="Describe potential co-branding opportunities..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Mission/Vision of Coworking Space</label>
              <Textarea
                name="mission_vision"
                value={formData.mission_vision}
                onChange={handleInputChange}
                placeholder="Share your mission and vision..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Community Values/Culture</label>
              <Textarea
                name="community_values"
                value={formData.community_values}
                onChange={handleInputChange}
                placeholder="Describe your community values and culture..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Testimonials from Previous Events or Members</label>
              <Textarea
                name="testimonials"
                value={formData.testimonials}
                onChange={handleInputChange}
                placeholder="Share testimonials or success stories..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Previous Events Info & Stats</label>
              <Textarea
                name="previous_events_info"
                value={formData.previous_events_info}
                onChange={handleInputChange}
                placeholder="Share information about previous events and statistics..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={3}
              />
            </div>

            <div className="bg-amber-600/10 border border-amber-600/20 rounded-lg p-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-white">Success Club Host Membership</h4>
                <div className="text-2xl font-bold text-green-500">FREE</div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Host your own exclusive community</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Revenue generation from community members</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Advanced community management tools</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Challenge creation and management</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Success Club magazine included</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="font-semibold text-green-400">No monthly fees - completely free!</span>
                </div>
              </div>
            </div>

            {errors.general && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-red-400 text-sm font-medium">{errors.general}</p>
                    {errors.general.includes("Email already registered") && (
                      <div className="mt-2 text-red-300 text-xs">
                        <p>💡 Try using a different email address, or</p>
                        <button
                          onClick={() => navigate('/login')}
                          className="text-red-400 hover:text-red-300 underline mt-1"
                        >
                          Go to login page if you already have an account
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="text-center py-8">
            <div className="w-24 h-24 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              Application Successfully Submitted!
            </h2>
            <div className="bg-amber-600 rounded-lg p-6 mb-6 text-center">
              <p className="text-lg font-semibold text-white mb-2">
                Host status must be approved by Success Club admin.
              </p>
              <p className="text-amber-100">
                Will be approved within 3 days.
              </p>
            </div>
            <div className="text-left bg-gray-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-white mb-3">What happens next:</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  Our admin team will review your application
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  You'll receive an email notification with the decision
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  Once approved, you can access your host portal
                </li>
              </ul>
            </div>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate('/login')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Back to Login
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Go to Homepage
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full shadow-lg overflow-hidden" style={{ borderRadius: '50%', width: '80px', height: '80px' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png"
                  alt="Success Club Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', scale: '1.1' }}
                />
              </div>
            </div>
            <CardTitle className="text-2xl text-white flex items-center justify-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              {currentStep === 4 ? 'Application Submitted!' : 'Host Registration'}
            </CardTitle>

            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {currentStep === 4 ? (
                // Success step - show completed checkmark
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-green-600 text-white">
                  <CheckCircle className="w-4 h-4" />
                </div>
              ) : (
                // Steps 1-3
                [1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === currentStep
                        ? 'bg-amber-600 text-white'
                        : step < currentStep
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-700 text-gray-400'
                      }`}
                  >
                    {step < currentStep ? <CheckCircle className="w-4 h-4" /> : step}
                  </div>
                ))
              )}
            </div>
            <p className="text-gray-400 mt-2">
              {currentStep === 4
                ? 'Application submitted successfully'
                : `Step ${currentStep} of 3`
              }
            </p>
          </CardHeader>

          <CardContent>
            {renderStep()}

            {/* Hide navigation buttons on success step */}
            {currentStep !== 4 && (
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-800">
                <div className="flex gap-2">
                  {currentStep > 1 && (
                    <Button
                      onClick={prevStep}
                      variant="outline"
                      className="border-gray-600 text-gray-300 hover:bg-gray-800"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                  )}

                  <Button
                    onClick={() => navigate('/auth')}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Cancel
                  </Button>
                </div>

                <div>
                  {currentStep < 3 ? (
                    <Button
                      onClick={nextStep}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {loading ? 'Creating Account...' : 'Complete Registration'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="auth-footer">
        <div className="auth-footer-content">
          <div className="auth-footer-links">
            <a href="/features">Features</a>
            <a href="/events">Events</a>
            <a href="/magazine">Magazine</a>
            <a href="/pricing">Pricing</a>
            <a href="/contact">Contact</a>
            <a href="/help-center">Help</a>
            <a href="/privacy-policy">Privacy</a>
          </div>
          <div className="auth-footer-copy">
            © 2025 Success Club. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HostSignup;